/**
 * Centralized dynamic UI terminology based on companyType and assetType in localStorage,
 * with optional per-record asset type override for detail views.
 * This is not a localization / multi-language feature.
 */

import { bumpTerminologyRevision } from '@/helpers/terminologyReactive';

export const STORAGE_KEYS = {
    COMPANY_TYPE: 'companyType',
    ASSET_TYPE: 'assetType',
    ASSET_TYPE_LEGACY: 'AssetType'
};

export const COMPANIES_AND_LLCS_LABEL = 'Companies & LLCs';

/** Supported asset types that replace generic "Asset" in UI text. */
export const SUPPORTED_ASSET_TYPES = [
    'Aircraft',
    'Apartment',
    'Boat',
    'Co-Investment & SPVs',
    'Co-Parenting',
    COMPANIES_AND_LLCS_LABEL,
    'Household',
    'Investor',
    'Married Parents',
    'RV',
    'Vacation Home'
];

/** @deprecated Use SUPPORTED_ASSET_TYPES â€” kept for backward-compatible imports */
export const ASSET_LABEL_CONFIG = Object.fromEntries(
    SUPPORTED_ASSET_TYPES.map((type) => [type.toLowerCase(), type])
);

/** Singular display label used in place of "Asset" when terminology is active. */
export const ASSET_SINGULAR_LABEL_CONFIG = {
    Aircraft: 'Aircraft',
    Apartment: 'Property',
    Boat: 'Boat',
    'Co-Investment & SPVs': 'Co-Investment',
    'Co-Parenting': 'Child',
    [COMPANIES_AND_LLCS_LABEL]: 'Company',
    Household: 'Place',
    'Married Parents': 'Child',
    RV: 'RV',
    'Vacation Home': 'Vacation Home'
};

/** Plural display label used in place of "Assets" when terminology is active. */
export const ASSET_PLURAL_LABEL_CONFIG = {
    Aircraft: 'Aircraft',
    Apartment: 'Properties',
    Boat: 'Boats',
    'Co-Investment & SPVs': 'Co-Investments',
    'Co-Parenting': 'Children',
    [COMPANIES_AND_LLCS_LABEL]: 'Companies',
    Household: 'Places',
    'Married Parents': 'Children',
    RV: 'RVs',
    'Vacation Home': 'Vacation Homes'
};

/** Menu label for booking-related navigation (by supported asset type). */
export const BOOKING_MENU_LABEL_CONFIG = {
    'Vacation Home': 'Bookings',
    RV: 'Trips',
    Boat: 'Trip',
    Aircraft: 'Flights'
};

/** @deprecated Use ASSET_LABEL_CONFIG â€” kept for backward-compatible imports */
export const COMPANY_TYPE_TERMINOLOGY_CONFIG = {
    rv: {
        owner: 'RV Owner',
        asset: 'RV Asset',
        details: 'RV Details'
    },
    vh: {
        owner: 'Vehicle Owner',
        asset: 'Vehicle Asset',
        details: 'Vehicle Details'
    },
    boat: {
        owner: 'Boat Owner',
        asset: 'Boat Asset',
        details: 'Boat Details'
    },
    Aircraft: {
        owner: 'Aircraft Owner',
        asset: 'Aircraft Asset',
        details: 'Aircraft Details'
    },
    'Vacation Home': {
        owner: 'Vacation Home Owner',
        asset: 'Vacation Home Asset',
        details: 'Vacation Home Details'
    },
    'Co-Investment & SPVs': {
        owner: 'Co-Investment owner',
        asset: 'Co-Investment asset',
        details: 'Co-Investment details'
    },
    'Co-Parenting': {
        owner: 'Child owner',
        asset: 'Child asset',
        details: 'Child details'
    },
    'Married Parents': {
        owner: 'Child owner',
        asset: 'Child asset',
        details: 'Child details'
    }
};

/** Same identifier as coOwnerSidebarMenu / report role access. Display label is not the role value. */
const CO_OWNER_USER_TYPE = 'usertype05';
/** Company Admin role. On Investor companies this role displays as Owner. */
const COMPANY_ADMIN_USER_TYPE = 'usertype02';

function readLoggedInUserType() {
    try {
        const fromStorage = localStorage.getItem('userType');
        if (fromStorage && String(fromStorage).trim()) {
            return String(fromStorage).trim().toLowerCase();
        }
        const parsed = JSON.parse(localStorage.getItem('userinfo') || 'null');
        return String(parsed?.data?.userType || '').trim().toLowerCase();
    } catch {
        return '';
    }
}

/** True for the co-owner role (usertype05). Every other role uses the owner asset label. */
function isCoOwnerRole() {
    return readLoggedInUserType() === CO_OWNER_USER_TYPE;
}

/** Investor company + logged-in Company Admin (usertype02). */
export function showsInvestorOwnerRole(assetType = resolveActiveAssetType(), userType = readLoggedInUserType()) {
    return isInvestorAssetTypeName(assetType)
        && String(userType || '').trim().toLowerCase() === COMPANY_ADMIN_USER_TYPE;
}

/**
 * Display-only. Investor + usertype02 shows "Owner" wherever the UI says "Company Admin".
 * Other asset types and roles keep the original text.
 */
export function formatInvestorCompanyAdminLabel(text, assetType = resolveActiveAssetType()) {
    if (text == null || typeof text !== 'string' || text === '') {
        return text;
    }
    if (!showsInvestorOwnerRole(assetType)) {
        return text;
    }
    return text
        .replace(/\bCompany Admins\b/gi, 'Owners')
        .replace(/\bCompany Admin\b/gi, 'Owner');
}

function isInvestorAssetTypeName(assetType) {
    return normalizeKey(assetType) === 'investor';
}

function terminologyCacheKey(assetType) {
    const base = normalizeKey(assetType) || '__default__';
    if (base !== 'investor') {
        return base;
    }
    return `${base}|${isCoOwnerRole() ? 'coowner' : 'owner'}`;
}

const COMPANY_VERTICAL_ALIASES = {
    rv: 'rv',
    vh: 'vh',
    boat: 'boat',
    'Married Parents': 'child',
    'Co-Parenting': 'child',
    'Co-Investment & SPVs': 'Co- Investment & SPVs',
    Aircraft: 'Aircraft',
    [COMPANIES_AND_LLCS_LABEL]: 'Companies'
};

export const DEFAULT_TERMINOLOGY = {
    owner: 'Owner',
    asset: 'Asset',
    details: 'Asset Details',
    bookingMenu: 'Bookings',
    bookingSingular: 'Booking'
};

let recordAssetTypeContext = null;
const terminologyCacheByAssetType = new Map();

function normalizeKey(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

function resolveAlias(value, aliasMap) {
    const normalized = normalizeKey(value);
    if (!normalized) {
        return '';
    }
    if (aliasMap[normalized]) {
        return aliasMap[normalized];
    }
    for (const [pattern, key] of Object.entries(aliasMap)) {
        if (normalized === pattern || normalized.includes(pattern)) {
            return key;
        }
    }
    return normalized;
}

export function bumpTerminologyRefresh() {
    bumpTerminologyRevision();
}

export function setRecordAssetTypeContext(assetType) {
    recordAssetTypeContext = assetType ? String(assetType).trim() : null;
    clearTerminologyCache();
}

export function clearRecordAssetTypeContext() {
    recordAssetTypeContext = null;
    clearTerminologyCache();
}

export function getRecordAssetTypeContext() {
    return recordAssetTypeContext;
}

export function resolveActiveAssetType() {
    if (recordAssetTypeContext) {
        const supportedRecord = resolveSupportedAssetType(recordAssetTypeContext);
        if (supportedRecord) {
            return supportedRecord;
        }
    }
    return getStoredAssetType();
}

/**
 * Resolves a record-scoped terminology asset type from a category/asset label.
 * Returns null when the value is not a supported asset type (caller should use global storage).
 */
export function resolveRecordTerminologyAssetType(categoryName) {
    if (categoryName == null || String(categoryName).trim() === '') {
        return null;
    }
    return resolveSupportedAssetType(categoryName);
}

export function getStoredCompanyType() {
    try {
        return localStorage.getItem(STORAGE_KEYS.COMPANY_TYPE);
    } catch {
        return null;
    }
}

/**
 * Returns the canonical supported asset type when the stored value matches
 * one of the supported types (case-insensitive), otherwise null.
 */
export function resolveSupportedAssetType(assetType = resolveActiveAssetType()) {
    const normalized = normalizeKey(assetType);
    if (!normalized) {
        return null;
    }

    return (
        SUPPORTED_ASSET_TYPES.find((type) => normalizeKey(type) === normalized) || null
    );
}

export function getStoredAssetType() {
    try {
        return (
            localStorage.getItem(STORAGE_KEYS.ASSET_TYPE) ||
            localStorage.getItem(STORAGE_KEYS.ASSET_TYPE_LEGACY)
        );
    } catch {
        return null;
    }
}

/** @deprecated Use resolveSupportedAssetType â€” kept for backward-compatible imports */
export function resolveAssetTypeKey(assetType = resolveActiveAssetType()) {
    const supported = resolveSupportedAssetType(assetType);
    return supported ? normalizeKey(supported) : '';
}

export function resolveCompanyVerticalKey(companyType = getStoredCompanyType()) {
    const normalized = normalizeKey(companyType);
    if (!normalized) {
        return '';
    }
    if (COMPANY_VERTICAL_ALIASES[normalized]) {
        return COMPANY_VERTICAL_ALIASES[normalized];
    }
    if (normalized === 'co-own' || normalized === 'co_own' || normalized === 'coown') {
        return '';
    }
    if (normalized === 'co-manage' || normalized === 'co_manage' || normalized === 'comanage') {
        return '';
    }
    return resolveAlias(companyType, COMPANY_VERTICAL_ALIASES);
}

/**
 * Returns true when the active asset type has configured UI terminology aliases.
 */
export function shouldApplyAssetTerminology(assetType = resolveActiveAssetType()) {
    return resolveSupportedAssetType(assetType) != null;
}

function shouldUseStaticTerminology(assetType = resolveActiveAssetType()) {
    return !shouldApplyAssetTerminology(assetType);
}

function getStaticTerminologyContext() {
    return {
        assetType: null,
        supportedAssetType: null,
        companyType: null,
        hasAssetTypeLabel: false,
        asset: DEFAULT_TERMINOLOGY.asset,
        assetPlural: 'Assets',
        owner: DEFAULT_TERMINOLOGY.owner,
        details: DEFAULT_TERMINOLOGY.details,
        bookingMenu: DEFAULT_TERMINOLOGY.bookingMenu,
        bookingSingular: DEFAULT_TERMINOLOGY.bookingSingular
    };
}

export function getAssetTypeLabel(assetType = resolveActiveAssetType()) {
    if (!shouldApplyAssetTerminology(assetType)) {
        return DEFAULT_TERMINOLOGY.asset;
    }

    const supported = resolveSupportedAssetType(assetType);
    if (!supported) {
        return DEFAULT_TERMINOLOGY.asset;
    }

    if (supported === 'Investor') {
        return isCoOwnerRole() ? 'Investment' : 'Property';
    }

    return ASSET_SINGULAR_LABEL_CONFIG[supported] || DEFAULT_TERMINOLOGY.asset;
}

export function getAssetTypePluralLabel(assetType = resolveActiveAssetType()) {
    if (!shouldApplyAssetTerminology(assetType)) {
        return 'Assets';
    }

    const supported = resolveSupportedAssetType(assetType);
    if (!supported) {
        return 'Assets';
    }

    if (supported === 'Investor') {
        return isCoOwnerRole() ? 'Investments' : 'Properties';
    }

    return ASSET_PLURAL_LABEL_CONFIG[supported] || 'Assets';
}

export function getAssetOwnerLabel(assetType = resolveActiveAssetType()) {
    if (!shouldApplyAssetTerminology(assetType)) {
        return DEFAULT_TERMINOLOGY.owner;
    }

    const label = getAssetTypeLabel(assetType);
    return `${label} Owner`;
}

export function getAssetDetailsLabel(assetType = resolveActiveAssetType()) {
    if (!shouldApplyAssetTerminology(assetType)) {
        return DEFAULT_TERMINOLOGY.details;
    }

    const label = getAssetTypeLabel(assetType);
    return `${label} Details`;
}

function hasResolvedAssetTypeLabel(assetType = resolveActiveAssetType()) {
    return shouldApplyAssetTerminology(assetType);
}

export function getTerminologyContextForAssetType(assetType = resolveActiveAssetType(), forceRefresh = false) {
    if (shouldUseStaticTerminology(assetType)) {
        return getStaticTerminologyContext();
    }

    const cacheKey = terminologyCacheKey(assetType);
    if (!forceRefresh && terminologyCacheByAssetType.has(cacheKey)) {
        return terminologyCacheByAssetType.get(cacheKey);
    }

    const supportedAssetType = resolveSupportedAssetType(assetType);
    const assetTypeLabel = getAssetTypeLabel(assetType);
    const assetTypePluralLabel = getAssetTypePluralLabel(assetType);
    const hasAssetTypeLabel = hasResolvedAssetTypeLabel(assetType);

    const bookingMenu = supportedAssetType
        ? (BOOKING_MENU_LABEL_CONFIG[supportedAssetType] || DEFAULT_TERMINOLOGY.bookingMenu)
        : DEFAULT_TERMINOLOGY.bookingMenu;

    let bookingSingular = DEFAULT_TERMINOLOGY.bookingSingular;
    if (bookingMenu === 'Trip' || bookingMenu === 'Flights') {
        bookingSingular = 'Bookings';
    }

    const context = {
        assetType,
        supportedAssetType,
        companyType: getStoredCompanyType(),
        hasAssetTypeLabel,
        asset: assetTypeLabel,
        assetPlural: assetTypePluralLabel,
        owner: getAssetOwnerLabel(assetType),
        details: getAssetDetailsLabel(assetType),
        bookingMenu,
        bookingSingular
    };

    terminologyCacheByAssetType.set(cacheKey, context);
    return context;
}

export function getTerminologyContext(forceRefresh = false) {
    return getTerminologyContextForAssetType(resolveActiveAssetType(), forceRefresh);
}

export function clearTerminologyCache() {
    terminologyCacheByAssetType.clear();
}

export function getBookingMenuLabel(assetType = resolveActiveAssetType()) {
    if (!shouldApplyAssetTerminology(assetType)) {
        return DEFAULT_TERMINOLOGY.bookingMenu;
    }

    const supported = resolveSupportedAssetType(assetType);
    if (!supported) {
        return DEFAULT_TERMINOLOGY.bookingMenu;
    }

    return BOOKING_MENU_LABEL_CONFIG[supported] || DEFAULT_TERMINOLOGY.bookingMenu;
}

export function getBookingSingularLabel(assetType = resolveActiveAssetType()) {
    if (!shouldApplyAssetTerminology(assetType)) {
        return DEFAULT_TERMINOLOGY.bookingSingular;
    }

    const menuLabel = getBookingMenuLabel(assetType);
    if (menuLabel === 'Trip' || menuLabel === 'Flights') {
        return 'Bookings';
    }

    return DEFAULT_TERMINOLOGY.bookingSingular;
}

/**
 * Noun for a sentence, taken from the existing booking menu label.
 * Bookings → Booking, Trips → Trip, Trip → Trip, Flights → Flight.
 */
export function getBookingSentenceNoun(assetType = resolveActiveAssetType()) {
    const menu = String(getBookingMenuLabel(assetType) || DEFAULT_TERMINOLOGY.bookingMenu).trim();
    if (!menu || menu === DEFAULT_TERMINOLOGY.bookingMenu) {
        return DEFAULT_TERMINOLOGY.bookingSingular;
    }
    if (menu.length > 1 && /s$/i.test(menu) && !/ss$/i.test(menu)) {
        return menu.slice(0, -1);
    }
    return menu;
}

/** Grammatically singular success text using the active booking terminology. */
export function getBookingAddedSuccessMessage(assetType = resolveActiveAssetType()) {
    return `${getBookingSentenceNoun(assetType)} has been added successfully.`;
}

/**
 * Boat / RV / Plane / Aircraft use Departure / Arrival instead of Check In / Check Out.
 * Plane is accepted as an alias of Aircraft (project storage often uses Aircraft).
 */
export function usesDepartureArrivalTerminology(assetType = resolveActiveAssetType()) {
    const supported = resolveSupportedAssetType(assetType);
    const raw = normalizeKey(supported || assetType || getStoredAssetType() || getRecordAssetTypeContext() || '');
    return raw === 'boat' || raw === 'rv' || raw === 'plane' || raw === 'aircraft';
}

export function getCheckInLabel(assetType = resolveActiveAssetType()) {
    return usesDepartureArrivalTerminology(assetType) ? 'Departure' : 'Check In';
}

export function getCheckOutLabel(assetType = resolveActiveAssetType()) {
    return usesDepartureArrivalTerminology(assetType) ? 'Arrival' : 'Check Out';
}

function applyCheckInOutReplacements(text) {
    if (!text || typeof text !== 'string') {
        return text;
    }

    const pairs = [
        ['Check-in/Check-out', 'Departure/Arrival'],
        ['Check-In/Check-Out', 'Departure/Arrival'],
        ['Check In / Check Out', 'Departure / Arrival'],
        ['Check-in / Check-out', 'Departure / Arrival'],
        ['Choose Check-in Date', 'Choose Departure Date'],
        ['Choose Check-out Date', 'Choose Arrival Date'],
        ['Check-in Date is required', 'Departure Date is required'],
        ['Check-out Date is required', 'Arrival Date is required'],
        ['Check-In Date', 'Departure Date'],
        ['Check-Out Date', 'Arrival Date'],
        ['Check-in Date', 'Departure Date'],
        ['Check-out Date', 'Arrival Date'],
        ['Check In Date', 'Departure Date'],
        ['Check Out Date', 'Arrival Date'],
        ['Check-In', 'Departure'],
        ['Check-Out', 'Arrival'],
        ['Check-in', 'Departure'],
        ['Check-out', 'Arrival'],
        ['Check In', 'Departure'],
        ['Check Out', 'Arrival']
    ];

    let result = text;
    for (const [from, to] of pairs) {
        if (from && result.includes(from)) {
            result = result.split(from).join(to);
        }
    }
    return result;
}

export function getOwnerLabel(assetType = resolveActiveAssetType()) {
    return getAssetOwnerLabel(assetType);
}

export function getAssetLabel(assetType = resolveActiveAssetType()) {
    return getAssetTypeLabel(assetType);
}

export function getDetailsLabel(assetType = resolveActiveAssetType()) {
    return getAssetDetailsLabel(assetType);
}

function buildAssetReplacementPairs(ctx) {
    if (!ctx.hasAssetTypeLabel) {
        return [];
    }

    const label = ctx.asset;
    const plural = ctx.assetPlural;

    return [
        ['Back to Assets', `Back to ${plural}`],
        ['No Assets Found', `No ${plural} Found`],
        ['No assets found', `No ${plural.toLowerCase()} found`],
        ['Loading assets...', `Loading ${plural.toLowerCase()}...`],
        ['No asset found', `No ${label.toLowerCase()} found`],
        ['Manage Owned Assets', `Manage Owned ${plural}`],
        ['Manage Assets', `Manage ${plural}`],
        ['Owned Assets', `Owned ${plural}`],
        ['My Assets', `My ${plural}`],
        ['All Assets', `All ${plural}`],
        ['Total Assets', `Total ${plural}`],
        ['Search Assets', `Search ${plural}`],
        ['Select Assets', `Select ${plural}`],
        ['View Assets', `View ${plural}`],
        ['Add New Asset', `Add New ${label}`],
        ['Create New Asset', `Create New ${label}`],
        ['Add First Asset', `Add First ${label}`],
        ['Add Asset', `Add ${label}`],
        ['Add Asset Closure', `Add ${label} Closure`],
        ['Create Asset', `Create ${label}`],
        ['Edit Asset', `Edit ${label}`],
        ['Update Asset', `Update ${label}`],
        ['View Asset', `View ${label}`],
        ['Delete Asset', `Delete ${label}`],
        ['Select Asset', `Select ${label}`],
        ['Search Asset', `Search ${label}`],
        ['Publish Asset', `Publish ${label}`],
        ['Asset Buy Process', `${label} Buy Process`],
        ['Asset Publish Request', `${label} Publish Request`],
        ['Asset Information', `${label} Information`],
        ['Asset Inventory', `${label} Inventory`],
        ['Asset Management', `${label} Management`],
        ['Asset Manager', `${label} Manager`],
        ['Asset Documents', `${label} Documents`],
        ['Asset Document', `${label} Document`],
        ['Asset Category', `${label} Category`],
        ['Asset Owner(s)', `${label} Owner(s)`],
        ['Asset Owners', `${label} Owners`],
        ['Asset Owner', `${label} Owner`],
        ['Asset Details', `${label} Details`],
        ['Asset Detail', `${label} Detail`],
        ['Asset Name', `${label} Name`],
        ['Asset Value', `${label} Value`],
        ['Asset Price', `${label} Price`],
        ['Asset Type', `${label} Type`],
        ['Asset List', `${label} List`],
        ['Asset Status', `${label} Status`],
        ['Asset Request', `${label} Request`],
        ['Asset Requests', `${label} Requests`],
        ['Asset Notes', `${label} Notes`],
        ['Asset History', `${label} History`],
        ['Asset Images', `${label} Images`],
        ['Vacant Assets', `Vacant ${plural}`],
        ['Occupied Assets', `Occupied ${plural}`],
        ['Property Details', `${label} Details`],
        ['Select Asset Name', `Select ${label} Name`],
        ['Select Asset Type', `Select ${label} Type`],
        ['Select Asset Status', `Select ${label} Status`],
        ['Select Asset', `Select ${label}`],
        ['Select Assets', `Select ${plural}`],
        ['Select Asset(s)', `Select ${label}(s)`],
        ['Please Select Asset', `Please Select ${label}`],
        ['Asset Type is required', `${label} Type is required`],
        ['Add Asset Owner', `Add ${label} Owner`],
        ['Add Asset Manager', `Add ${label} Manager`],
        ['Manage Asset Calendar', `Manage ${label} Calendar`],
        ['Manage Asset Mapping', `Manage ${label} Mapping`],
        ['Manage Asset Reviews', `Manage ${label} Reviews`],
        ['Manage Asset Categories', `Manage ${label} Categories`],
        ['Add Asset Category', `Add ${label} Category`],
        ['Edit Asset Category', `Edit ${label} Category`],
        ['First Asset', `First ${label}`],
        ['Update Asset Information', `Update ${label} Information`],
        ['Update Company Information', `Update ${label} Information`],
        ['Add Company Information', `Add ${label} Information`],
        ['No, Add Asset Owner', `No, Add ${label} Owner`],
        ['Asset Calendar', `${label} Calendar`],
        ['Asset Mapping', `${label} Mapping`],
        ['Asset Reviews', `${label} Reviews`],
        ['Asset Categories', `${label} Categories`],
        ['Asset Category Attribute', `${label} Category Attribute`],
        ['Owned Asset', `Owned ${label}`],
        ['Mapped Asset', `Mapped ${label}`],
        ['Asset Detial', `${label} Detial`],
        ['Asset Detial View', `${label} Detial View`],
        ['Asset Location is required', `${label} Location is required`],
        ['Assets Name', `${plural} Name`],
        ['Assets Type', `${plural} Type`],
        ['Show only checklists for the selected Asset Type', `Show only checklists for the selected ${label} Type`]
    ];
}

function buildReplacementPairs(ctx) {
    const pairs = [];
    const bookingMenu = ctx.bookingMenu;
    const bookingSingular = ctx.bookingSingular;

    const bookingPhrases = [
        ['Manage Bookings', `Manage ${bookingMenu}`],
        ['View Bookings', `View ${bookingMenu}`],
        ['Total Bookings', `Total ${bookingMenu}`],
        ['Canceled Bookings', `Canceled ${bookingMenu}`],
        ['On Quotes & Bookings', `On Quotes & ${bookingMenu}`],
        ['Quotes & Bookings', `Quotes & ${bookingMenu}`],
        ['Resume Bookings', `Resume ${bookingMenu}`],
        ['Confirmed Bookings', `Confirmed ${bookingMenu}`],
        ['Booking Requests', `${bookingSingular} Requests`],
        ['Customer Booking details', `Customer ${bookingSingular} details`],
        ['Customer Booking Details', `Customer ${bookingSingular} Details`],
        ['Booking Details', `${bookingSingular} Details`],
        ['Booking details', `${bookingSingular} details`],
        ['Booking Information', `${bookingSingular} Information`],
        ['Add Booking', `Add ${bookingSingular}`],
        ['Update Booking Status', `Update ${bookingSingular} Status`],
        ['Booking Status', `${bookingSingular} Status`],
        ['Bookings', bookingMenu],
        ['Booking', bookingSingular]
    ];

    pairs.push(...bookingPhrases);
    pairs.push(...buildAssetReplacementPairs(ctx));
    if (ctx.supportedAssetType === 'Household') {
        // Longer ownership phrases first â€” otherwise "Co-owner" â†’ "Roommate" turns
        // "Co-ownership shares" into the broken "Roommateship shares".
        pairs.push(
            ['Co-ownership shares', 'Roommates'],
            ['Co-ownership share', 'Roommate'],
            ['Roommateship shares', 'Roommates'],
            ['Roommateship share', 'Roommate'],
            ['Add Co-Owner', 'Add Roommate'],
            ['Add Co-owner', 'Add Roommate'],
            ['Co-Owners', 'Roommates'],
            ['Co-owners', 'Roommates'],
            ['co-owners', 'roommates'],
            ['Co-Owner', 'Roommate'],
            ['Co-owner', 'Roommate'],
            ['co-owner', 'roommate'],
            ['Co Owners', 'Roommates'],
            ['co owners', 'roommates'],
            ['Co Owner', 'Roommate'],
            ['co owner', 'roommate']
        );
    } else if (ctx.supportedAssetType && ctx.supportedAssetType !== 'Married Parents') {
        // Non-household: Chore â†’ Task, Roommate â†’ Co-owner.
        // Married Parents uses its own labels (Household Tasks, Co-Parents) below.
        // Longer phrases first so plural forms stay intact.
        pairs.push(
            ['Chores', 'Tasks'],
            ['chores', 'tasks'],
            ['Chore', 'Task'],
            ['chore', 'task'],
            ['Roommates', 'Co-owners'],
            ['roommates', 'co-owners'],
            ['Roommate', 'Co-owner'],
            ['roommate', 'co-owner']
        );
    }

    return pairs;
}

/**
 * Apartment display labels.
 * "Asset" is already mapped to Property/Properties by the shared label config.
 * Literal Apartment/Apartments text uses the same words, with existing case handling.
 * Co-Owner stays Co-Owner for every other asset type (Household → Roommate, Investor → Investors).
 * Word boundaries keep "Co-ownership" intact.
 */
function applyApartmentDisplayTerminology(text) {
    if (!text || typeof text !== 'string') {
        return text;
    }

    return text
        .replace(/\bApartments\b/g, 'Properties')
        .replace(/\bapartments\b/g, 'properties')
        .replace(/\bApartment\b/g, 'Property')
        .replace(/\bapartment\b/g, 'property')
        .replace(/\bco[\s-]?owners\b/gi, 'Tenants')
        .replace(/\bco[\s-]?owner\b/gi, 'Tenant');
}

/**
 * Investor-only people labels.
 * Word boundaries keep "Co-ownership" intact.
 * Section titles "Manage Co-Owners" and "Manage Company Users" display as "Investors".
 */
function applyInvestorPeopleTerminology(text) {
    if (!text || typeof text !== 'string') {
        return text;
    }

    const replacements = [
        [/\bManage Company Users\b/g, 'Investors'],
        [/\bCompany Users\b/g, 'Investors'],
        [/\bCompany User\b/g, 'Investors'],
        [/\bAsset Co-Owners\b/g, 'Investors'],
        [/\bAsset Co-Owner\b/g, 'Investors'],
        [/\bManage Co-Owners\b/g, 'Investors'],
        [/\bManage Co-owners\b/g, 'Investors'],
        [/\bManage Co-Owner\b/g, 'Investors'],
        [/\bManage Co-owner\b/g, 'Investors'],
        [/\bCo-Owners\b/g, 'Investors'],
        [/\bCo-owners\b/g, 'Investors'],
        [/\bco-owners\b/g, 'investors'],
        [/\bCo-Owner\b/g, 'Investors'],
        [/\bCo-owner\b/g, 'Investors'],
        [/\bco-owner\b/g, 'investors'],
        [/\bCo Owners\b/g, 'Investors'],
        [/\bCo owners\b/g, 'Investors'],
        [/\bco owners\b/g, 'investors'],
        [/\bCo Owner\b/g, 'Investors'],
        [/\bCo owner\b/g, 'Investors'],
        [/\bco owner\b/g, 'investors']
    ];

    let result = formatInvestorCompanyAdminLabel(text, 'Investor');
    for (const [pattern, to] of replacements) {
        result = result.replace(pattern, to);
    }
    return result;
}

/**
 * Married Parents display labels for the pages opened from the family dashboard.
 * Longer phrases run first. Lookbehinds keep "Household Expenses" from becoming
 * "Household Household Expenses".
 */
function applyMarriedParentsDisplayTerminology(text) {
    if (!text || typeof text !== 'string') {
        return text;
    }

    const phrases = [
        ['Manage Document Vault', 'Family Vault'],
        ['View Document Vault', 'Family Vault'],
        ['Document Vault', 'Family Vault'],
        ['Meetings Scheduler', 'Family Meetings'],
        ['Create Meeting Schedule', 'Create Family Meeting'],
        ['Create Meeting', 'Create Family Meeting'],
        ['Manage Recurring Expenses', 'Recurring Household Expenses'],
        ['Recurring Expenses', 'Recurring Household Expenses'],
        ['Manage Expenses', 'Household Expenses'],
        ['View Recurring Expense', 'View Recurring Household Expense'],
        ['Expense Settings', 'Household Expense Settings'],
        ['Total Expenses', 'Total Household Expenses'],
        ['Outstanding Expenses', 'Outstanding Household Expenses'],
        ['Collected Expenses', 'Collected Household Expenses'],
        ['Edit Expense', 'Edit Household Expense'],
        ['Add Expense', 'Add Household Expense'],
        ['Manage Chores', 'Household Tasks'],
        ['Manage Tasks', 'Household Tasks'],
        ["This Week's Chores", "This Week's Household Tasks"],
        ["This Week's Tasks", "This Week's Household Tasks"],
        ['No chores scheduled for this week.', 'No household tasks scheduled for this week.'],
        ['No tasks scheduled for this week.', 'No household tasks scheduled for this week.'],
        ['All Chores', 'All Household Tasks'],
        ['All Tasks', 'All Household Tasks'],
        ['My Chores', 'My Household Tasks'],
        ['My Tasks', 'My Household Tasks'],
        ['Add Chore', 'Add Household Task'],
        ['Add Task', 'Add Household Task'],
        ['Add Ticket', 'Add Household Task'],
        ['Edit Chore', 'Edit Household Task'],
        ['Edit Task', 'Edit Household Task'],
        ['Chore History', 'Household Task History'],
        ['Task History', 'Household Task History'],
        ['Chore details', 'Household Task details'],
        ['Chore Detail', 'Household Task Detail'],
        ['Chore Title', 'Household Task Title'],
        ['Update Chore Status', 'Update Household Task Status'],
        ['Select Chore Status', 'Select Household Task Status'],
        ['Chore Status', 'Household Task Status'],
        ['Delete Chore', 'Delete Household Task'],
        ['delete this chore', 'delete this household task'],
        ['enter a chore title', 'enter a household task title'],
        ['Chore title cannot', 'Household task title cannot'],
        ['Total Tickets', 'Total Household Tasks'],
        ['Closed Tickets', 'Closed Household Tasks'],
        ['Pending Tickets', 'Pending Household Tasks'],
        ['Open Tickets', 'Open Household Tasks'],
        ['Scheduled Meetings', 'Scheduled Family Meetings'],
        ["Today's Meetings", "Today's Family Meetings"],
        ['Update Meeting Status', 'Update Family Meeting Status'],
        ['Select Meeting Status', 'Select Family Meeting Status'],
        ['Select meeting status', 'Select family meeting status'],
        ['Meeting Status', 'Family Meeting Status'],
        ['Approve Meeting', 'Approve Family Meeting'],
        ['Reject Meeting', 'Reject Family Meeting'],
        ['Add Meeting', 'Add Family Meeting'],
        ['Meeting Detail', 'Family Meeting Detail'],
        ['Meeting title', 'Family Meeting title'],
        ['Meeting Title', 'Family Meeting Title'],
        ['Meeting time', 'Family Meeting time'],
        ['Meeting Time', 'Family Meeting Time'],
        ['Assigned Roommate', 'Assigned Parent'],
        ['Assign Co-owner', 'Assign Parent'],
        ['Select Co-owner', 'Select Parent'],
        ['select a co-owner', 'select a parent'],
        ['Add Co-Owner', 'Add Parent'],
        ['Add Co-owner', 'Add Parent'],
        ['Edit Co-Owner', 'Edit Parent'],
        ['Edit Co-owner', 'Edit Parent'],
        ['Manage Co-Owners', 'Parents'],
        ['Manage Co-owners', 'Parents'],
        ['Co-Owners', 'Parents'],
        ['Co-owners', 'Parents'],
        ['co-owners', 'parents'],
        ['Co-Owner', 'Parent'],
        ['Co-owner', 'Parent'],
        ['co-owner', 'parent'],
        ['owner', 'parent'],
        ['Company Admins', 'Parents'],
        ['Company Admin', 'Parent']
    ];

    let result = text;
    for (const [from, to] of phrases) {
        if (result.includes(from)) {
            result = result.split(from).join(to);
        }
    }

    const wordRules = [
        [/(?<!Household )(?<!household )\bExpenses\b/g, 'Household Expenses'],
        [/(?<!Household )(?<!household )\bexpenses\b/g, 'household expenses'],
        [/(?<!Household )(?<!household )\bExpense\b/g, 'Household Expense'],
        [/(?<!Household )(?<!household )\bexpense\b/g, 'household expense'],
        [/(?<!Household )(?<!household )\bTickets\b/g, 'Household Tasks'],
        [/(?<!Household )(?<!household )\btickets\b/g, 'household tasks'],
        [/(?<!Household )(?<!household )\bTicket\b/g, 'Household Task'],
        [/(?<!Household )(?<!household )\bticket\b/g, 'household task'],
        [/(?<!Household )(?<!household )\bChores\b/g, 'Household Tasks'],
        [/(?<!Household )(?<!household )\bchores\b/g, 'household tasks'],
        [/(?<!Household )(?<!household )\bChore\b/g, 'Household Task'],
        [/(?<!Household )(?<!household )\bchore\b/g, 'household task'],
        [/(?<!Family )(?<!family )\bMeetings\b/g, 'Family Meetings'],
        [/(?<!Family )(?<!family )\bmeetings\b/g, 'family meetings'],
        [/(?<!Family )(?<!family )\bMeeting\b/g, 'Family Meeting'],
        [/(?<!Family )(?<!family )\bmeeting\b/g, 'family meeting'],
        [/\bRoommates\b/g, 'Parents'],
        [/\broommates\b/g, 'parents'],
        [/\bRoommate\b/g, 'Parent'],
        [/\broommate\b/g, 'parent'],
        [/\bFamily Admins\b/g, 'Parents'],
        [/\bFamily Admin\b/g, 'Parent'],
        [/\bCo-Parents\b/g, 'Parents'],
        [/\bco-parents\b/g, 'parents'],
        [/\bCo-Parent\b/g, 'Parent'],
        [/\bco-parent\b/g, 'parent'],
        [/\bco-parents\b/gi, 'Parents'],
        [/\bco-parent\b/gi, 'Parent']
    ];

    if (/^calend[ae]r$/i.test(result.trim())) {
        return 'Family Calendar';
    }

    for (const [pattern, to] of wordRules) {
        result = result.replace(pattern, to);
    }
    return result;
}

/**
 * Co-Parenting display labels. Admin is Co-parent, co-owners are Members,
 * expenses are Child Expenses, and meetings stay Meeting.
 */
function applyCoParentingDisplayTerminology(text) {
    if (!text || typeof text !== 'string') {
        return text;
    }

    const phrases = [
        ['Meetings Scheduler', 'Meeting'],
        ['Manage Recurring Expenses', 'Recurring Child Expenses'],
        ['Recurring Expenses', 'Recurring Child Expenses'],
        ['Manage Expenses', 'Child Expenses'],
        ['Total Expenses', 'Total Child Expenses'],
        ['Outstanding Expenses', 'Outstanding Child Expenses'],
        ['Collected Expenses', 'Collected Child Expenses'],
        ['Edit Expense', 'Edit Child Expense'],
        ['Add Expense', 'Add Child Expense'],
        ['Scheduled Meetings', 'Scheduled Meeting'],
        ["Today's Meetings", "Today's Meeting"],
        ['Add Co-Owner', 'Add Parent'],
        ['Add Co-owner', 'Add Parent'],
        ['Edit Co-Owner', 'Edit Parent'],
        ['Edit Co-owner', 'Edit Parent'],
        ['Manage Co-Owners', 'Parents'],
        ['Manage Co-owners', 'Parents'],
        ['Co-Owners', 'Parents'],
        ['Co-owners', 'Parents'],
        ['co-owners', 'parents'],
        ['Co-Owner', 'Parent'],
        ['Co-owner', 'Parent'],
        ['co-owner', 'parent'],
        ['Company Admins', 'Parents'],
        ['Company Admin', 'Parent']
    ];

    let result = text;
    for (const [from, to] of phrases) {
        if (result.includes(from)) {
            result = result.split(from).join(to);
        }
    }

    const wordRules = [
        [/(?<!Child )(?<!child )\bExpenses\b/g, 'Child Expenses'],
        [/(?<!Child )(?<!child )\bexpenses\b/g, 'child expenses'],
        [/(?<!Child )(?<!child )\bExpense\b/g, 'Child Expense'],
        [/(?<!Child )(?<!child )\bexpense\b/g, 'child expense'],
        [/\bMeetings\b/g, 'Meeting'],
        [/\bmeetings\b/g, 'meeting'],
        [/\bFamily Admins\b/g, 'Parents'],
        [/\bFamily Admin\b/g, 'Parent'],
        [/\bCo-Parents\b/g, 'Parents'],
        [/\bco-parents\b/g, 'parents'],
        [/\bCo-Parent\b/g, 'Parent'],
        [/\bco-parent\b/g, 'parent'],
        [/\bco-parents\b/gi, 'Parents'],
        [/\bco-parent\b/gi, 'Parent'],
        [/\bMembers\b/g, 'Parents'],
        [/\bmembers\b/g, 'parents'],
        [/\bMember\b/g, 'Parent'],
        [/\bmember\b/g, 'parent']
    ];

    for (const [pattern, to] of wordRules) {
        result = result.replace(pattern, to);
    }
    return result;
}

/** Display-only. Leaves the text unchanged unless the active asset type is Investor. */
export function applyInvestorTerminology(text, assetType = resolveActiveAssetType()) {
    if (!isInvestorAssetTypeName(assetType)) {
        return text;
    }
    return applyDynamicTerminology(text, assetType);
}

/**
 * Untranslated sidebar module names skip i18n, so Investor terminology is applied here.
 * Translated labels already pass through i18n.global.t.
 */
export function applyUntranslatedMenuTerminology(moduleName, hasTranslation) {
    if (hasTranslation || moduleName == null) {
        return moduleName;
    }
    if (!isInvestorAssetTypeName(getStoredAssetType())) {
        return moduleName;
    }
    return applyDynamicTerminology(moduleName);
}

/**
 * Returns true when the string looks like an untranslated i18n key path.
 */
export function isUntranslatedI18nKey(text, key = null) {
    if (text == null || typeof text !== 'string') {
        return false;
    }
    if (key && text === key) {
        return true;
    }
    return /^[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)+$/.test(text);
}

export const ONBOARDING_ASSET_MANAGER_STEP_LABEL = 'Add Asset Manager';
export const ONBOARDING_GUARDIAN_STEP_LABEL = 'Add Guardian';
export const ONBOARDING_ASSET_MANAGER_DETAILS_TITLE = "Asset Manager's Details";
export const ONBOARDING_GUARDIAN_DETAILS_TITLE = "Guardian's Details";
export const ONBOARDING_ASSET_MANAGER_INTRO_QUESTION = 'Do you want to add and assign Asset Manager?';
export const ONBOARDING_GUARDIAN_INTRO_QUESTION = 'Do you want to add and assign Guardian?';

/**
 * Household-only onboarding step display labels (title / description).
 * Keys are canonical phrases after normalizeTerminologyForMatching.
 */
export const ONBOARDING_HOUSEHOLD_STEP_LABEL_CONFIG = {
    'add first asset': 'Add Place',
    'register your first asset': 'Register your place',
    'add existing co-owner': 'Add Roommate',
    'direct onboard existing co-owner': 'Direct onboard roommate'
};

/** @deprecated Use ONBOARDING_HOUSEHOLD_STEP_LABEL_CONFIG â€” kept for backward-compatible imports */
export const ONBOARDING_FIRST_ASSET_LABEL_CONFIG = {
    Household: {
        title: ONBOARDING_HOUSEHOLD_STEP_LABEL_CONFIG['add first asset'],
        description: ONBOARDING_HOUSEHOLD_STEP_LABEL_CONFIG['register your first asset']
    }
};

const GUARDIAN_ONBOARDING_ASSET_TYPES = new Set([
    'co-parenting',
    'married parents',
    'married parent'
]);

/** Child types that show "Add Guardian" instead of "Add Asset Manager" in onboarding step labels. */
export function usesGuardianOnboardingStepLabel(assetType) {
    const resolved = resolveSupportedAssetType(assetType);
    if (resolved === 'Co-Parenting' || resolved === 'Married Parents') {
        return true;
    }
    return GUARDIAN_ONBOARDING_ASSET_TYPES.has(normalizeKey(assetType));
}

/** Child types that use Guardian / parent-specific onboarding asset terminology and behavior. */
export function isParentOnboardingAssetType(assetType) {
    return usesGuardianOnboardingStepLabel(assetType);
}

export function isAddAssetManagerStepLabel(text) {
    return normalizeKey(text) === normalizeKey(ONBOARDING_ASSET_MANAGER_STEP_LABEL);
}

/**
 * Household-only onboarding step display overrides (e.g. Step 1 Place, Step 4 Roommate).
 * Returns null when the label should use the normal terminology pipeline.
 */
export function resolveHouseholdOnboardingStepLabel(text, assetType = resolveActiveAssetType()) {
    if (resolveSupportedAssetType(assetType) !== 'Household') {
        return null;
    }
    if (text == null || String(text).trim() === '') {
        return null;
    }

    const canonicalText = normalizeKey(normalizeTerminologyForMatching(text));
    return ONBOARDING_HOUSEHOLD_STEP_LABEL_CONFIG[canonicalText] || null;
}

/**
 * @deprecated Use resolveHouseholdOnboardingStepLabel â€” kept for backward-compatible imports
 */
export function resolveHouseholdOnboardingFirstAssetLabel(text, assetType = resolveActiveAssetType()) {
    return resolveHouseholdOnboardingStepLabel(text, assetType);
}

/** Onboarding-only display label for the Add Asset Manager step. */
export function resolveOnboardingAssetManagerStepDisplayLabel(assetType) {
    if (usesGuardianOnboardingStepLabel(assetType)) {
        return ONBOARDING_GUARDIAN_STEP_LABEL;
    }
    return applyDynamicTerminology(ONBOARDING_ASSET_MANAGER_STEP_LABEL, assetType);
}

/** Onboarding wizard heading for the asset manager details step. */
export function resolveOnboardingAssetManagerDetailsTitle(assetType = resolveActiveAssetType()) {
    if (usesGuardianOnboardingStepLabel(assetType)) {
        return ONBOARDING_GUARDIAN_DETAILS_TITLE;
    }
    return applyDynamicTerminology(ONBOARDING_ASSET_MANAGER_DETAILS_TITLE, assetType);
}

/** Onboarding wizard intro question for the asset manager step. */
export function resolveOnboardingAssetManagerIntroQuestion(assetType = resolveActiveAssetType()) {
    if (usesGuardianOnboardingStepLabel(assetType)) {
        return ONBOARDING_GUARDIAN_INTRO_QUESTION;
    }
    return applyDynamicTerminology(ONBOARDING_ASSET_MANAGER_INTRO_QUESTION, assetType);
}

/**
 * Maps display-only terminology synonyms back to canonical business terms for matching.
 * Use this in conditions/comparisons â€” never use applyDynamicTerminology output for business logic.
 */
export function normalizeTerminologyForMatching(text) {
    let result = String(text || '').toLowerCase();
    // Household: Roommate â†” Co-owner (display-only synonym)
    result = result.replace(/\broommates\b/g, 'co-owners');
    result = result.replace(/\broommate\b/g, 'co-owner');
    // Asset-type singular/plural display labels â†” Asset
    result = result.replace(/\bplaces\b/g, 'assets');
    result = result.replace(/\bplace\b/g, 'asset');
    result = result.replace(/\bchildren\b/g, 'assets');
    result = result.replace(/\bchild\b/g, 'asset');
    result = result.replace(/\bco-investments\b/g, 'assets');
    result = result.replace(/\bco-investment\b/g, 'asset');
    result = result.replace(/\bcompanies\b/g, 'assets');
    result = result.replace(/\bcompany\b/g, 'asset');
    result = result.replace(/\bvacation homes\b/g, 'assets');
    result = result.replace(/\bvacation home\b/g, 'asset');
    result = result.replace(/\brvs\b/g, 'assets');
    result = result.replace(/\brv\b/g, 'asset');
    result = result.replace(/\bboats\b/g, 'assets');
    result = result.replace(/\bboat\b/g, 'asset');
    result = result.replace(/\baircraft\b/g, 'asset');
    result = result.replace(/\bapartments\b/g, 'assets');
    result = result.replace(/\bapartment\b/g, 'asset');
    if (resolveSupportedAssetType(resolveActiveAssetType()) === 'Apartment') {
        result = result.replace(/\bproperties\b/g, 'assets');
        result = result.replace(/\bproperty\b/g, 'asset');
        result = result.replace(/\btenants\b/g, 'co-owners');
        result = result.replace(/\btenant\b/g, 'co-owner');
    }
    if (isInvestorAssetTypeName(resolveActiveAssetType())) {
        result = result.replace(/\binvestors\b/g, 'co-owners');
        result = result.replace(/\binvestor\b/g, 'co-owner');
        result = result.replace(/\binvestments\b/g, 'assets');
        result = result.replace(/\binvestment\b/g, 'asset');
        result = result.replace(/\bproperties\b/g, 'assets');
        result = result.replace(/\bproperty\b/g, 'asset');
    }
    return result;
}

/**
 * True when an onboarding step title contains any needle, using both the raw title
 * and a terminology-normalized form (so "Roommate"/"House" display labels still match).
 */
export function onboardingStepTitleIncludes(title, ...needles) {
    const raw = String(title || '').toLowerCase();
    if (!raw) {
        return false;
    }
    const normalized = normalizeTerminologyForMatching(raw);
    return needles.some((needle) => {
        const n = String(needle || '').toLowerCase();
        return n && (raw.includes(n) || normalized.includes(n));
    });
}

/**
 * Breadcrumb, confirmation, and toast text.
 * Property/property uses the existing asset label. booking/Booking uses the existing booking label.
 */
export function applyMessageTerminology(text, assetType = resolveActiveAssetType()) {
    if (text == null || typeof text !== 'string' || text === '') {
        return text == null ? '' : text;
    }

    let result = text;
    result = result.replace(/\bProperties\b/g, 'Assets');
    result = result.replace(/\bproperties\b/g, 'assets');
    result = result.replace(/\bProperty\b/g, 'Asset');
    result = result.replace(/\bproperty\b/g, 'asset');
    result = result.replace(/\bbookings\b/gi, 'Bookings');
    result = result.replace(/\bbooking\b/gi, 'Bookings');

    const applied = applyDynamicTerminology(result, assetType);
    return typeof applied === 'string' ? applied : '';
}

/**
 * Applies configured terminology replacements to already-resolved UI display text.
 * @param {string} text
 * @param {string|null} assetType Optional asset type override (record scope).
 */
export function applyDynamicTerminology(text, assetType = resolveActiveAssetType()) {
    if (text == null || typeof text !== 'string' || text === '') {
        return text;
    }

    if (isUntranslatedI18nKey(text)) {
        return text;
    }

    let result = text;

    // Boat / RV / Plane / Aircraft: Check In/Out → Departure/Arrival
    if (usesDepartureArrivalTerminology(assetType)) {
        result = applyCheckInOutReplacements(result);
    }

    if (!shouldApplyAssetTerminology(assetType)) {
        return result;
    }

    const ctx = getTerminologyContextForAssetType(assetType);

    for (const [from, to] of buildReplacementPairs(ctx)) {
        if (from && result.includes(from)) {
            result = result.split(from).join(to);
        }
    }

    if (ctx.supportedAssetType === 'Investor') {
        result = applyInvestorPeopleTerminology(result);
    }

    if (ctx.supportedAssetType === 'Apartment') {
        result = applyApartmentDisplayTerminology(result);
    }

    if (ctx.supportedAssetType === 'Married Parents') {
        result = applyMarriedParentsDisplayTerminology(result);
    }

    if (ctx.supportedAssetType === 'Co-Parenting') {
        result = applyCoParentingDisplayTerminology(result);
    }

    if (ctx.hasAssetTypeLabel) {
        result = result.replace(/\bAssets\b/g, ctx.assetPlural);
        result = result.replace(/\bassets\b/g, ctx.assetPlural.toLowerCase());
        result = result.replace(/\bAsset\b/g, ctx.asset);
        result = result.replace(/\basset\b/g, ctx.asset.toLowerCase());

        if (ctx.supportedAssetType === COMPANIES_AND_LLCS_LABEL) {
            result = result.replace(/Companies & LLCs/g, ctx.asset);
        }
    }

    return result;
}

/**
 * Resolves i18n/dynamicTranslate output and applies terminology in one step.
 */
export function translateDisplayText(value, key, fallback, assetType = resolveActiveAssetType()) {
    const resolved = value != null && key != null && value !== key ? value : fallback;
    return applyDynamicTerminology(resolved, assetType);
}

/**
 * Resolves a logical UI label key to the configured display string.
 */
const APARTMENT_ASSET_TYPE_NAMES = new Set(['apartment', 'appartment', 'apartmentdera']);

function readBrowserHost() {
    try {
        return String(location.host || '').trim().toLowerCase();
    } catch {
        return '';
    }
}

function configuredHost(envName) {
    return String(process.env[envName] || '').trim().toLowerCase();
}

/** Apartment / Appartment, including the ApartmentDera vertical key. Display name is not used as an id. */
export function isApartmentAssetTypeValue(value) {
    const normalized = normalizeKey(value);
    if (!normalized) {
        return false;
    }
    if (APARTMENT_ASSET_TYPE_NAMES.has(normalized)) {
        return true;
    }
    return resolveSupportedAssetType(value) === 'Apartment';
}

/** Existing portal host check for the ApartmentDera company vertical. */
export function isApartmentDeraHost(host = readBrowserHost()) {
    const current = String(host || '').trim().toLowerCase();
    if (!current) {
        return false;
    }
    const configured = configuredHost('VUE_APP_APARTMENTDERA_HOST');
    if (configured && current === configured) {
        return true;
    }
    return current.includes('apartmentdera');
}

function isOtherDeraHost(host = readBrowserHost()) {
    const current = String(host || '').trim().toLowerCase();
    if (!current || isApartmentDeraHost(current)) {
        return false;
    }
    const otherHosts = [
        'VUE_APP_ROOMDERA_HOST',
        'VUE_APP_BOATDERA_HOST',
        'VUE_APP_PLANEDERA_HOST',
        'VUE_APP_KIDSDERA_HOST',
        'VUE_APP_BIZDERA_HOST',
        'VUE_APP_PARENTDERA_HOST',
        'VUE_APP_HOMEDERA_HOST',
        'VUE_APP_RVDERA_HOST'
    ].map(configuredHost).filter(Boolean);
    return otherHosts.includes(current);
}

/**
 * True only for the ApartmentDera company vertical when the current asset type is Apartment.
 * Other Dera hosts keep their existing labels even if a record name contains "Apartment".
 * Local and generic hosts use the stored login asset type, which is the existing frontend signal.
 */
export function isApartmentDeraApartment(explicitAssetType) {
    const stored = getStoredAssetType();
    const storedIsApartment = isApartmentAssetTypeValue(stored);
    const onApartmentHost = isApartmentDeraHost();
    const companyIsApartmentDera = onApartmentHost || (storedIsApartment && !isOtherDeraHost());
    if (!companyIsApartmentDera) {
        return false;
    }

    const current = explicitAssetType != null && String(explicitAssetType).trim() !== ''
        ? explicitAssetType
        : (getRecordAssetTypeContext() || stored);
    const currentIsSupported = resolveSupportedAssetType(current);
    if (currentIsSupported && currentIsSupported !== 'Apartment') {
        return false;
    }
    if (isApartmentAssetTypeValue(current)) {
        return true;
    }
    return storedIsApartment || onApartmentHost;
}
/**
 * Company-user screens use Co-Owner as the role name.
 * Married Parents and Co-Parenting display Parent / Parents.
 * Every other asset type keeps the original text.
 */
function companyUserCoOwnerLabels(assetType = getStoredAssetType()) {
    const supported = resolveSupportedAssetType(assetType || getStoredAssetType());
    if (supported === 'Married Parents' || supported === 'Co-Parenting') {
        return {
            singular: 'Parent',
            plural: 'Parents',
            lower: 'parent',
            lowerPlural: 'parents'
        };
    }
    return null;
}

/** Singular co-owner display label, or null when the stored asset type should stay unchanged. */
export function getCompanyUserCoOwnerLabel(assetType = getStoredAssetType()) {
    return companyUserCoOwnerLabels(assetType)?.singular || null;
}

/** Asset Manager display label for KIDS companies, or null for every other asset type. */
export function getCompanyUserKidsManagerLabel(assetType = getStoredAssetType()) {
    const supported = resolveSupportedAssetType(assetType || getStoredAssetType());
    if (supported === 'Married Parents' || supported === 'Co-Parenting') {
        return 'Kids Manager';
    }
    return null;
}

function replaceDisplayPhrase(text, from, to) {
    const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(`(?<![A-Za-z0-9])${escaped}(?![A-Za-z0-9])`, 'g'), to);
}

/**
 * Display-only. Rewrites Co-Owner / company-user role wording for Married Parents and Co-Parenting.
 * Other asset types are returned unchanged. Does not affect ids, payloads, or validation rules.
 */
export function formatCompanyUserRoleText(text, assetType = getStoredAssetType()) {
    if (text == null || typeof text !== 'string' || text === '') {
        return text;
    }
    const labels = companyUserCoOwnerLabels(assetType);
    if (!labels) {
        return text;
    }

    const { singular, plural, lower, lowerPlural } = labels;
    const kidsManagerLabel = getCompanyUserKidsManagerLabel(assetType);
    const pairs = [
        ['Child managers', kidsManagerLabel || 'Kids Manager'],
        ['Child Managers', kidsManagerLabel || 'Kids Manager'],
        ['Child manager', kidsManagerLabel || 'Kids Manager'],
        ['Child Manager', kidsManagerLabel || 'Kids Manager'],
        ['child managers', kidsManagerLabel || 'Kids Manager'],
        ['child manager', kidsManagerLabel || 'Kids Manager'],
        ['Asset managers', kidsManagerLabel || 'Kids Manager'],
        ['Asset Managers', kidsManagerLabel || 'Kids Manager'],
        ['Asset manager', kidsManagerLabel || 'Kids Manager'],
        ['Asset Manager', kidsManagerLabel || 'Kids Manager'],
        ['asset managers', kidsManagerLabel || 'Kids Manager'],
        ['asset manager', kidsManagerLabel || 'Kids Manager'],
        ['Child owners', plural],
        ['Child Owners', plural],
        ['Child owner', singular],
        ['Child Owner', singular],
        ['child owners', lowerPlural],
        ['child owner', lower],
        ['Asset owners', plural],
        ['Asset Owners', plural],
        ['Asset owner', singular],
        ['Asset Owner', singular],
        ['asset owners', lowerPlural],
        ['asset owner', lower],
        ['Manage Company Users', `${singular} List`],
        ['Add/Edit Company User', `Add/Edit ${singular}`],
        ['Delete Company User', `Delete ${singular}`],
        ['Active Company User', `Active ${singular}`],
        ['In-Active Company User', `In-Active ${singular}`],
        ['Add Company User', `Add ${singular}`],
        ['Edit Company User', `Edit ${singular}`],
        ['Company Users', plural],
        ['Company User', singular],
        ['Co-Owner List', `${singular} List`],
        ['Co-owner List', `${singular} List`],
        ['co-owner list', `${lower} list`],
        ['Co-Owner Name', `${singular} Name`],
        ['Co-owner Name', `${singular} Name`],
        ['co-owner name', `${lower} name`],
        ['Add Co-Owners', `Add ${plural}`],
        ['Add Co-owners', `Add ${plural}`],
        ['Add Co-Owner', `Add ${singular}`],
        ['Add Co-owner', `Add ${singular}`],
        ['Edit Co-Owners', `Edit ${plural}`],
        ['Edit Co-Owner', `Edit ${singular}`],
        ['Edit Co-owner', `Edit ${singular}`],
        ['Co-Owners', plural],
        ['Co-owners', plural],
        ['co-owners', lowerPlural],
        ['Co-Owner', singular],
        ['Co-owner', singular],
        ['co-owner', lower],
        ['Co Owners', plural],
        ['Co owners', plural],
        ['co owners', lowerPlural],
        ['Co Owner', singular],
        ['Co owner', singular],
        ['co owner', lower],
        ['Create User', `Add ${singular}`],
        ['Add User', `Add ${singular}`],
        ['Edit User', `Edit ${singular}`],
        ['Manage Users', `${singular} List`],
        ['Select User Type', `Select ${singular}`],
        ['User Type is required. Please select one', `${singular} is required`],
        ['User Type is required. Please select atleast one', `${singular} is required`],
        ['Are you sure you want to delete user', `Are you sure you want to delete ${lower}`],
        ['User has been added successfully.', `${singular} has been added successfully.`],
        ['User has been added successfully', `${singular} has been added successfully`],
        ['User has been updated successfully.', `${singular} has been updated successfully.`],
        ['User has been updated successfully', `${singular} has been updated successfully`],
        ['Failed to save user', `Failed to save ${lower}`],
        ['Error adding Co-Owner', `Error adding ${singular}`],
        ['Error updating Co-Owner', `Error updating ${singular}`],
        ['Error deleting Co-Owner', `Error deleting ${singular}`],
        ['Error adding User', `Error adding ${singular}`],
        ['Error updating User', `Error updating ${singular}`],
        ['Error deleting User', `Error deleting ${singular}`],
        ['Error adding user', `Error adding ${lower}`],
        ['Error updating user', `Error updating ${lower}`],
        ['Error deleting user', `Error deleting ${lower}`]
    ];

    let result = text;
    for (const [from, to] of pairs) {
        result = replaceDisplayPhrase(result, from, to);
    }
    result = result.replace(/\bco[\s-]?owners\b/gi, plural);
    result = result.replace(/\bco[\s-]?owner\b/gi, singular);
    result = result.replace(/\bcompany\s*admins\b/gi, plural);
    result = result.replace(/\bcompany\s*admin\b/gi, singular);
    result = result.replace(/\bfamily\s*admins\b/gi, plural);
    result = result.replace(/\bfamily\s*admin\b/gi, singular);
    result = result.replace(/\bco[\s-]?parents\b/gi, plural);
    result = result.replace(/\bco[\s-]?parent\b/gi, singular);
    result = result.replace(/\bmembers\b/gi, plural);
    result = result.replace(/\bmember\b/gi, singular);
    result = result.replace(/\bowners\b/gi, plural);
    result = result.replace(/\bowner\b/gi, singular);
    return result;
}
export function getUiLabel(key, assetType = resolveActiveAssetType()) {
    const normalized = normalizeKey(key).replace(/\s+/g, '');

    const ctx = getTerminologyContextForAssetType(assetType);

    switch (normalized) {
        case 'bookings':
        case 'bookingmenu':
        case 'bookingmenulabel':
            return ctx.bookingMenu;
        case 'booking':
        case 'bookingsingular':
            return ctx.bookingSingular;
        case 'owner':
        case 'ownerlabel':
            return ctx.owner;
        case 'asset':
        case 'assetlabel':
            return ctx.asset;
        case 'assets':
        case 'assetplural':
        case 'assetplurallabel':
            return ctx.assetPlural;
        case 'details':
        case 'detailslabel':
        case 'assetdetails':
            return ctx.details;
        default:
            return applyDynamicTerminology(key, assetType);
    }
}

const dynamicUiText = {
    STORAGE_KEYS,
    SUPPORTED_ASSET_TYPES,
    COMPANIES_AND_LLCS_LABEL,
    ASSET_LABEL_CONFIG,
    ASSET_SINGULAR_LABEL_CONFIG,
    ASSET_PLURAL_LABEL_CONFIG,
    BOOKING_MENU_LABEL_CONFIG,
    COMPANY_TYPE_TERMINOLOGY_CONFIG,
    DEFAULT_TERMINOLOGY,
    getStoredCompanyType,
    getStoredAssetType,
    resolveSupportedAssetType,
    resolveAssetTypeKey,
    resolveCompanyVerticalKey,
    resolveActiveAssetType,
    resolveRecordTerminologyAssetType,
    setRecordAssetTypeContext,
    clearRecordAssetTypeContext,
    getRecordAssetTypeContext,
    shouldApplyAssetTerminology,
    getTerminologyContext,
    getTerminologyContextForAssetType,
    clearTerminologyCache,
    bumpTerminologyRefresh,
    getBookingMenuLabel,
    getBookingSingularLabel,
    getBookingSentenceNoun,
    getBookingAddedSuccessMessage,
    usesDepartureArrivalTerminology,
    getCheckInLabel,
    getCheckOutLabel,
    getAssetTypeLabel,
    getAssetTypePluralLabel,
    getAssetOwnerLabel,
    getAssetDetailsLabel,
    getOwnerLabel,
    getAssetLabel,
    getDetailsLabel,
    isUntranslatedI18nKey,
    ONBOARDING_ASSET_MANAGER_STEP_LABEL,
    ONBOARDING_GUARDIAN_STEP_LABEL,
    ONBOARDING_ASSET_MANAGER_DETAILS_TITLE,
    ONBOARDING_GUARDIAN_DETAILS_TITLE,
    ONBOARDING_ASSET_MANAGER_INTRO_QUESTION,
    ONBOARDING_GUARDIAN_INTRO_QUESTION,
    ONBOARDING_HOUSEHOLD_STEP_LABEL_CONFIG,
    ONBOARDING_FIRST_ASSET_LABEL_CONFIG,
    usesGuardianOnboardingStepLabel,
    isParentOnboardingAssetType,
    isAddAssetManagerStepLabel,
    resolveHouseholdOnboardingStepLabel,
    resolveHouseholdOnboardingFirstAssetLabel,
    resolveOnboardingAssetManagerStepDisplayLabel,
    resolveOnboardingAssetManagerDetailsTitle,
    resolveOnboardingAssetManagerIntroQuestion,
    normalizeTerminologyForMatching,
    onboardingStepTitleIncludes,
    applyDynamicTerminology,
    isApartmentAssetTypeValue,
    isApartmentDeraHost,
    isApartmentDeraApartment,
    showsInvestorOwnerRole,
    formatInvestorCompanyAdminLabel,
    applyInvestorTerminology,
    applyUntranslatedMenuTerminology,
    applyMessageTerminology,
    translateDisplayText,
    getCompanyUserCoOwnerLabel,
    getCompanyUserKidsManagerLabel,
    formatCompanyUserRoleText,
    getUiLabel
};

export default dynamicUiText;
